import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { scoreFieldChanged } from "@/redux/slices/dreamUniversitySlice";
import { AlertTriangle, Calculator, Info, ListChecks, Scale } from "lucide-react";
import { useTranslation } from "react-i18next";
import BackControls from "../components/BackControls";
import type { College, Department, Gender, Track } from "../data/sharedTypes";
import { getCollegeNameKey } from "../data/sharedTypes";
import type { UniversityId } from "../data/universityRegistry";
import { getRequiredFields, getScoreInfoAlert, getTrackMinHighSchool, getUniversityInfo, isCollegeMinHardGate, isFlatStructure } from "../data/universityRegistry";
import { getImHsBelowAcceptedLine } from "../data/internalMissionsData";
import type { Scores } from "../data/sharedTypes";
import { calculateEquivalentRate } from "../utils/calculateEquivalentRate";
import StepHeader from "../StepHeader";

interface Props {
    universityId: UniversityId;
    college: College;
    selectedDepartment?: Department | null;
    selectedTrack?: Track | null;
    selectedGender?: Gender | null;
    onBack: () => void;
    onResult: (scores: Scores, rate: number) => void;
}

const fieldLabels: Record<string, string> = {
    high_school: "dream_university_high_school",
    english: "dream_university_english",
    math: "dream_university_math",
    arabic: "dream_university_arabic",
    chemistry: "dream_university_chemistry",
    french: "dream_university_french",
};

const fieldWeightKeys: Record<string, string> = {
    high_school: "highSchoolWeight",
    english: "englishWeight",
    math: "mathWeight",
    arabic: "arabicWeight",
    chemistry: "chemistryWeight",
};

export default function ScoreCalculator({ universityId, college, selectedDepartment, selectedTrack, selectedGender, onBack, onResult }: Props) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const effectiveWeights = selectedDepartment?.weightsOverride ?? college.weights;
    const requiredFields = getRequiredFields(universityId, effectiveWeights);
    const extraField = selectedDepartment?.extraScoreField;

    // Entered numbers persist in redux so the student finds them again after
    // navigating away or reloading; they only reset when the university
    // changes or a newly picked specialty needs a different number of inputs.
    const scores = useAppSelector((state) => state.dreamUniversity.scores);

    const getWeight = (field: string): number => {
        const key = fieldWeightKeys[field];
        return (effectiveWeights as unknown as Record<string, number>)[key] || 0;
    };

    const trackMinHs = getTrackMinHighSchool(universityId, selectedTrack);
    const universityInfo = getUniversityInfo(universityId);
    const isFlat = isFlatStructure(universityId);

    // Each section owns its score-step info/warning alert (wording, percentages,
    // and show/hide rules) in its data module; it is rendered via
    // getScoreInfoAlert below. The shared soft-warning mislabels IM's values,
    // so IM is suppressed there.
    const isInternalMissions = universityId === "internal_missions";

    // When the section enforces college-min as a hard apply-floor (AASU /
    // PAAET / FM), missing the college's minHighSchool blocks the step. When
    // it's only competitive guidance (KU / IM), the same condition produces
    // a soft amber warning and the student can still proceed.
    const collegeMinIsHard = isCollegeMinHardGate(universityId, college.key);
    // Soft-mode means: track-min is the hard gate, college/dept mins are
    // guidance. Preserved as a single boolean so the existing soft-warning
    // branches below keep working.
    const isKuMode = !collegeMinIsHard && trackMinHs !== null;
    // KU Science & Law surface last year's accepted rate as a target only — it
    // is not a score-step apply-floor, so no dept/college warning is shown and
    // the student can calculate below it; the shortfall is reported on the
    // results step instead.
    const isKuSciLaw =
        universityId === "kuwait_university" && (college.key === "science" || college.key === "law");

    const allFieldsValid = requiredFields.every((field) => {
        const val = parseFloat(scores[field] || "");
        return !isNaN(val) && val >= 0 && val <= 100;
    });

    const hsVal = parseFloat(scores.high_school || "");

    // Department/specialty minimum HS from admission conditions. KU Science &
    // Law and Internal Missions have no score-step apply-floor (their rate is
    // last year's accepted target — guidance only), so deptMinHs is null there.
    const deptMinHs = isKuSciLaw || isInternalMissions
        ? null
        : selectedDepartment?.minHighSchool
        ?? (selectedDepartment && selectedDepartment.equivalentRate > 0 && selectedDepartment.type === "high_school"
            ? selectedDepartment.equivalentRate
            : null);

    // University-level track minimum (e.g., KU: 70% scientific, 78% literary) — check first
    const hsBelowTrackMin =
        trackMinHs !== null &&
        !isNaN(hsVal) &&
        hsVal > 0 &&
        hsVal < trackMinHs;

    const hsBelowDeptMin =
        !hsBelowTrackMin &&
        deptMinHs !== null &&
        !isNaN(hsVal) &&
        hsVal > 0 &&
        hsVal < deptMinHs;

    // College-specific minimum (e.g., Medicine: 85%). When the college has a
    // literary-specific threshold (AASU Business Admin: 80% sci / 85% lit),
    // pick the active track's value.
    const collegeMinHs =
        selectedTrack === "literary" && effectiveWeights.minHighSchoolLiterary != null
            ? effectiveWeights.minHighSchoolLiterary
            : effectiveWeights.minHighSchool;
    const hsBelowCollegeMin =
        !hsBelowTrackMin &&
        !hsBelowDeptMin &&
        collegeMinHs !== null &&
        !isNaN(hsVal) &&
        hsVal > 0 &&
        hsVal < collegeMinHs;

    const hsSoftBelow = isKuMode && (hsBelowDeptMin || hsBelowCollegeMin);
    const acceptedRate = hsBelowDeptMin
        ? deptMinHs
        : hsBelowCollegeMin
            ? collegeMinHs
            : null;

    const hsHardBlock = hsBelowTrackMin || (!isKuMode && (hsBelowDeptMin || hsBelowCollegeMin));

    // Internal Missions shows last year's lowest accepted HS as soft guidance
    // only — an amber inline note below the HS input; calculation is never
    // blocked by it.
    const imBelowAcceptedLine = isInternalMissions
        ? getImHsBelowAcceptedLine(selectedDepartment, hsVal)
        : null;

    const engVal = parseFloat(scores.english || "");
    const engBelowMin =
        effectiveWeights.minEnglish !== null &&
        !isNaN(engVal) &&
        engVal > 0 &&
        engVal < effectiveWeights.minEnglish;

    const allFieldsFilled = allFieldsValid && !hsHardBlock && !engBelowMin;

    const handleCalculate = () => {
        const parsedScores: Scores = {
            high_school: parseFloat(scores.high_school) || 0,
            english: parseFloat(scores.english) || 0,
            math: parseFloat(scores.math) || 0,
            arabic: parseFloat(scores.arabic) || 0,
            chemistry: parseFloat(scores.chemistry) || 0,
            french: parseFloat(scores.french) || 0,
        };

        const rate = calculateEquivalentRate(universityId, effectiveWeights, parsedScores);
        onResult(parsedScores, rate);
    };

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_enter_scores")}
                subtitle={`${college.emoji} ${t(getCollegeNameKey(college))}`}
            />

            {/* Selection trail */}
            <div className="flex flex-wrap items-center justify-center gap-2">
                {universityInfo && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                        <span>{universityInfo.emoji}</span>
                        {t(universityInfo.nameKey)}
                    </span>
                )}
                {(() => {
                    const gender = selectedGender ?? selectedDepartment?.gender ?? null;
                    if (!gender) return null;
                    return (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ring-1 ${
                            gender === "female"
                                ? "bg-pink-50 text-pink-700 ring-pink-200"
                                : "bg-sky-50 text-sky-700 ring-sky-200"
                        }`}>
                            {t(`dream_university_gender_${gender}`)}
                        </span>
                    );
                })()}
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-theme-50 text-theme-700 ring-1 ring-theme-200">
                    <span>{college.emoji}</span>
                    {t(getCollegeNameKey(college))}
                </span>
                {(() => {
                    const track = selectedDepartment?.track ?? selectedTrack ?? null;
                    if (!track) return null;
                    return (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ring-1 ${
                            track === "scientific"
                                ? "bg-blue-50 text-blue-700 ring-blue-200"
                                : "bg-purple-50 text-purple-700 ring-purple-200"
                        }`}>
                            {t(`dream_university_track_${track}`)}
                            {trackMinHs !== null && (
                                <> · {t("dream_university_track_min_hs", { percent: trackMinHs })}</>
                            )}
                        </span>
                    );
                })()}
                {selectedDepartment?.departmentKey && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        {universityId === "internal_missions"
                            ? t(`dream_university_institution_${selectedDepartment.departmentKey}`)
                            : isFlat
                                ? t(`dream_university_country_${selectedDepartment.departmentKey}`)
                                : t(`dream_university_dept_${selectedDepartment.departmentKey}`)}
                    </span>
                )}
                {(() => {
                    // Resolve a "competitive" min-HS that complements the track-min chip.
                    //   - When trackMinHs is set, only surface a separate chip if the
                    //     college's competitive threshold differs (e.g. AASU 70% floor
                    //     vs 85% Business Admin literary).
                    //   - When trackMinHs is null, surface the dept/college minimum
                    //     directly (PAAET / FM with min-to-apply).
                    const competitiveMin =
                        selectedTrack === "literary" && effectiveWeights.minHighSchoolLiterary != null
                            ? effectiveWeights.minHighSchoolLiterary
                            : effectiveWeights.minHighSchool;
                    const deptMin = selectedDepartment?.minHighSchool
                        ?? (selectedDepartment?.type === "high_school" && selectedDepartment.equivalentRate > 0
                            ? selectedDepartment.equivalentRate
                            : null);
                    const minHs = competitiveMin ?? deptMin;
                    if (minHs === null) return null;
                    if (trackMinHs !== null && trackMinHs === minHs) return null;
                    return (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 ring-1 ring-orange-200">
                            {selectedDepartment?.passOnly
                                ? t("dream_university_specialty_just_pass_hs")
                                : universityId === "kuwait_university" && (college.key === "science" || college.key === "law")
                                    ? t("dream_university_ku_score_last_year_accepted", { rate: minHs })
                                    : universityId === "internal_missions"
                                        ? t(
                                            selectedDepartment?.type === "equivalent"
                                                ? "dream_university_im_eng_last_year_rate_card"
                                                : "dream_university_im_noneng_last_year_hs_card",
                                            { rate: minHs },
                                        )
                                        : universityId === "paaet"
                                            ? t("dream_university_paaet_specialty_min_hs_to_apply", { rate: minHs })
                                            : t("dream_university_specialty_min_hs_to_apply", { rate: minHs })}
                        </span>
                    );
                })()}
            </div>

            {(() => {
                const result = getScoreInfoAlert(universityId, college, selectedDepartment ?? null, selectedTrack, collegeMinHs, hsVal);
                if (!result) {
                    return null;
                }
                const alerts = Array.isArray(result) ? result : [result];
                const iconMap = {
                    "info": Info,
                    "calculator": Calculator,
                    "list-checks": ListChecks,
                    "scale": Scale,
                    "alert-triangle": AlertTriangle,
                } as const;
                const toneMap = {
                    info: { box: "bg-blue-50 border-blue-200", icon: "text-blue-500", text: "text-blue-700" },
                    error: { box: "bg-red-50 border-red-200", icon: "text-red-500", text: "text-red-700" },
                    warning: { box: "bg-amber-50 border-amber-200", icon: "text-amber-500", text: "text-amber-700" },
                    tip: { box: "bg-indigo-50 border-indigo-200", icon: "text-indigo-500", text: "text-indigo-700" },
                } as const;
                return (
                    <div className="flex flex-col gap-y-3">
                        {alerts.map((alert, idx) => {
                            const tone = toneMap[alert.severity] ?? toneMap.info;
                            const Icon = alert.iconKey
                                ? iconMap[alert.iconKey]
                                : alert.severity === "info" || alert.severity === "tip"
                                    ? Info
                                    : AlertTriangle;
                            return (
                                <div key={idx} className={`flex items-start gap-3 ${tone.box} border rounded-xl p-4`}>
                                    <Icon className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} />
                                    <div className="space-y-1">
                                        {alert.titleKey && (
                                            <p className={`text-sm font-semibold ${tone.text}`}>{t(alert.titleKey)}</p>
                                        )}
                                        {alert.lines.map((line, i) => (
                                            <p key={i} className={`text-sm ${tone.text}`}>{t(line.key, line.params)}</p>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            })()}

            {hsBelowTrackMin && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-red-700">
                            {t("dream_university_track_min_alert_title")}
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                            {t("dream_university_track_min_alert_body", {
                                university: universityInfo ? t(universityInfo.nameKey) : "",
                                required: trackMinHs,
                                track: t(`dream_university_track_${selectedTrack}`),
                            })}
                        </p>
                    </div>
                </div>
            )}

            {!isKuMode && hsBelowDeptMin && deptMinHs !== null && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-red-700">
                            {t("dream_university_dept_min_alert_title")}
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                            {t("dream_university_dept_min_alert_body", {
                                required: deptMinHs,
                            })}
                        </p>
                    </div>
                </div>
            )}

            {hsSoftBelow && acceptedRate !== null && trackMinHs !== null && !isInternalMissions && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">
                        {t("dream_university_below_accepted_rate_body", {
                            minHs: trackMinHs,
                            acceptedRate,
                        })}
                    </p>
                </div>
            )}

            {extraField && selectedDepartment?.minExtraScore != null && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">
                        {t("dream_university_extra_required_hint", {
                            required: selectedDepartment.minExtraScore,
                            field: t(fieldLabels[extraField]),
                        })}
                    </p>
                </div>
            )}

            <div className="bg-gray-50 rounded-2xl p-5">
                <p className="text-sm font-semibold text-gray-900 mb-4">
                    {t("dream_university_enter_scores")}
                </p>

                <div className="space-y-4">
                    {requiredFields.map((field) => {
                        const val = parseFloat(scores[field] || "");
                        const fieldHsBelowTrack =
                            field === "high_school" && hsBelowTrackMin;
                        const fieldHsBelowDept =
                            field === "high_school" && !hsBelowTrackMin && hsBelowDeptMin;
                        const fieldHsBelowCollege =
                            field === "high_school" &&
                            !hsBelowTrackMin &&
                            !hsBelowDeptMin &&
                            hsBelowCollegeMin;
                        const fieldHsSoftBelow =
                            field === "high_school" && isKuMode && (fieldHsBelowDept || fieldHsBelowCollege);
                        const fieldImBelowAccepted =
                            field === "high_school" && imBelowAcceptedLine !== null;
                        const showEngWarning =
                            field === "english" &&
                            effectiveWeights.minEnglish !== null &&
                            !isNaN(val) &&
                            val > 0 &&
                            val < effectiveWeights.minEnglish;
                        const showHardError = fieldHsBelowTrack
                            || (!isKuMode && (fieldHsBelowDept || fieldHsBelowCollege))
                            || showEngWarning;

                        return (
                            <div key={field}>
                                <div className="relative">
                                    <label className="absolute top-2 ltr:right-3 rtl:left-3 text-xs text-gray-500">
                                        {t(fieldLabels[field])}
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={scores[field] || ""}
                                        onChange={(e) =>
                                            dispatch(scoreFieldChanged({
                                                field,
                                                value: e.target.value,
                                            }))
                                        }
                                        placeholder={t(fieldLabels[field])}
                                        className={`w-full pt-7 pb-3 px-3 bg-white border rounded-xl text-gray-900 font-medium focus:ring-1 transition-colors text-start ${
                                            showHardError
                                                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                                                : fieldHsSoftBelow || fieldImBelowAccepted
                                                    ? "border-amber-300 focus:border-amber-500 focus:ring-amber-500"
                                                    : "border-gray-200 focus:border-theme-500 focus:ring-theme-500"
                                        }`}
                                    />
                                </div>
                                {fieldHsBelowTrack ? (
                                    <p className="text-xs text-red-500 mt-1">
                                        {t("dream_university_hs_below_track_minimum", {
                                            entered: val,
                                            required: trackMinHs,
                                            track: t(`dream_university_track_${selectedTrack}`),
                                        })}
                                    </p>
                                ) : fieldImBelowAccepted && imBelowAcceptedLine ? (
                                    <p className="text-xs text-amber-600 mt-1">
                                        {t(imBelowAcceptedLine.key, imBelowAcceptedLine.params)}
                                    </p>
                                ) : fieldHsSoftBelow && acceptedRate !== null ? (
                                    <p className="text-xs text-amber-600 mt-1">
                                        {t("dream_university_below_accepted_rate_inline", {
                                            entered: val,
                                            acceptedRate,
                                        })}
                                    </p>
                                ) : fieldHsBelowDept ? (
                                    <p className="text-xs text-red-500 mt-1">
                                        {t("dream_university_hs_below_dept_minimum", {
                                            entered: val,
                                            required: deptMinHs,
                                        })}
                                    </p>
                                ) : fieldHsBelowCollege ? (
                                    <p className="text-xs text-red-500 mt-1">
                                        {t("dream_university_hs_below_minimum", {
                                            entered: val,
                                            required: collegeMinHs,
                                        })}
                                    </p>
                                ) : showEngWarning ? (
                                    <p className="text-xs text-red-500 mt-1">
                                        {t("dream_university_eng_below_minimum", {
                                            entered: val,
                                            required: effectiveWeights.minEnglish,
                                        })}
                                    </p>
                                ) : (
                                    <p className="text-xs text-gray-500 mt-1 ltr:text-right rtl:text-left">
                                        {t("dream_university_weight_percentage")}{" "}
                                        {getWeight(field)}%
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <button
                onClick={handleCalculate}
                disabled={!allFieldsFilled}
                className="w-full py-3.5 bg-theme-700 text-white font-semibold rounded-xl hover:bg-theme-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
                {t("dream_university_calculate_rate")}
            </button>
        </div>
    );
}
